import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CalendarIcon, Building2, Euro, Clock, Edit, Trash2, User } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProposalDocuments } from "./ProposalDocuments";
import { ProposalStatusWorkflow } from "./ProposalStatusWorkflow";
import { format } from "date-fns";
import { statusDisplayMap, getStatusColor, getStatusIcon } from "@/constants/proposalMeta";

interface ProposalDetailsProps {
  proposal: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange?: (newStatus: string) => void;
}

export function ProposalDetails({ proposal, open, onOpenChange, onEdit, onDelete, onStatusChange }: ProposalDetailsProps) {
  if (!proposal) return null;

  const StatusIcon = getStatusIcon(proposal.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">Proposal Details</DialogTitle>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="workflow">Status & Workflow</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Header Info */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-semibold mb-2">{proposal.title}</h2>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Building2 className="h-4 w-4" />
                    <span>{proposal.clients?.name}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Euro className="h-4 w-4" />
                    <span className="font-medium">€{Number(proposal.value).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <Badge className={getStatusColor(proposal.status)}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusDisplayMap[proposal.status as keyof typeof statusDisplayMap]}
              </Badge>
            </div>

            <Separator />

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Dates */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Important Dates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Created</p>
                    <p className="text-sm">
                      {format(new Date(proposal.created_at), "MMM dd, yyyy")}
                    </p>
                  </div>
                  {proposal.sent_date && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Sent Date</p>
                      <p className="text-sm">
                        {format(new Date(proposal.sent_date), "MMM dd, yyyy")}
                      </p>
                    </div>
                  )}
                  {proposal.response_deadline && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Deadline</p>
                      <p className="text-sm">
                        {format(new Date(proposal.response_deadline), "MMM dd, yyyy")}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Client Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Building2 className="h-4 w-4 mr-2" />
                    Client Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Company</p>
                    <p className="text-sm font-medium">{proposal.clients?.name}</p>
                  </div>
                  {proposal.clients?.industry && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">Industry</p>
                      <p className="text-sm">{proposal.clients.industry}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Creator Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Created By
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">User</p>
                    <p className="text-sm font-medium">
                      {proposal.profiles?.full_name || 
                       `${proposal.profiles?.first_name || ''} ${proposal.profiles?.last_name || ''}`.trim() || 
                       'Unknown User'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Description */}
            {proposal.description && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {proposal.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Tags */}
            {proposal.tags && proposal.tags.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Tags</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {proposal.tags.map((tag: string, index: number) => (
                      <Badge key={index} variant="secondary">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="workflow" className="mt-6">
            <ProposalStatusWorkflow
              proposalId={proposal.id}
              currentStatus={proposal.status}
              onStatusChange={(newStatus) => {
                onStatusChange?.(newStatus);
                // Update the local proposal data
                proposal.status = newStatus;
              }}
            />
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <ProposalDocuments proposalId={proposal.id} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}